import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { SageItem } from '../../types/sageItem';
import { sageItemService } from '../../services/sageItemService';
import useDebouncedValue from '../../hooks/useDebouncedValue';

export interface SageItemAutocompleteProps {
  value: number | null;
  onChange: (sageItemId: number | null, item?: SageItem) => void;
  /** Pre-loaded items to skip the API fetch. Pass to avoid redundant network calls. */
  preloadedItems?: SageItem[];
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

export const SageItemAutocomplete: React.FC<SageItemAutocompleteProps> = ({
  value,
  onChange,
  preloadedItems,
  label = 'Sage Item',
  disabled = false,
  error = false,
  helperText,
  size = 'small',
}) => {
  const [items, setItems] = useState<SageItem[]>(preloadedItems ?? []);
  const [loading, setLoading] = useState(!preloadedItems);
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebouncedValue(inputValue, 300);

  // Load all active items once on mount (skip if preloaded)
  useEffect(() => {
    if (preloadedItems) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs items when the preloadedItems prop changes after mount; deriving during render would drop the fetched-items fallback path
      setItems(preloadedItems);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadItems = async () => {
      setLoading(true);
      try {
        const response = await sageItemService.getAllLocal();
        if (!cancelled && response.success) {
          setItems(response.data.filter((item) => item.active));
        }
      } catch {
        // Failed to load sage items - continue with empty list
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [preloadedItems]);

  // Client-side filtering with debounced search
  const filteredItems = useMemo(() => {
    if (!debouncedInput.trim()) return items;
    const term = debouncedInput.toLowerCase();
    return items.filter(
      (item) =>
        item.description.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term),
    );
  }, [items, debouncedInput]);

  // Find the currently selected item by value (id)
  const selectedItem = useMemo(() => {
    if (value === null) return null;
    return items.find((item) => item.id === value) ?? null;
  }, [items, value]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: SageItem | null) => {
      onChange(newValue?.id ?? null, newValue ?? undefined);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
    },
    [],
  );

  return (
    <Autocomplete
      value={selectedItem}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={filteredItems}
      getOptionLabel={(option) => option.description}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      size={size}
      noOptionsText={
        debouncedInput.trim()
          ? `No items matching "${debouncedInput}"`
          : 'No active items available'
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={500}>
                {option.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.code}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};
